import React, { useState, useEffect, useMemo } from 'react';
import { useLayout } from '../context/LayoutContext';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
  Chip,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Autocomplete,
  Breadcrumbs,
  Link as MuiLink,
  FormControlLabel,
   Switch 
} from '@mui/material';
import {
  Add,
  CalendarToday,
  People,
  DateRange,
  NavigateNext,
  Edit,
  Delete,
  CheckCircle,
  PlayArrow,
  Refresh,
  Visibility,
  Download,
  Print,
  ArrowBack,
  ArrowForward,
  WatchLater
} from '@mui/icons-material';

import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Import hooks
import {
  useCreateRollingRosterMutation,
  useGetRollingRostersQuery,
  useUpdateRollingRosterMutation,
  useGenerateAvailabilitiesMutation,
  useUpdateRollingRosterPeriodMutation,
  useGenerateAvailabilitiesForPeriodMutation,
  useSetNextGenerationDateMutation,
  useDeleteRollingRosterMutation
} from '../slices/rollingRosterApiSlice';
import { useGetUsersQuery } from '../slices/usersApiSlice';

import ReactDOM from 'react-dom';
import RollingRosterPrintView from './RollingRosterPrintView';

// Import availability components for reuse
import AvailabilityDialog from './AvailabilityDialog';

// Reuse your SHIFT_PRESETS and UNAVAILABILITY_REASONS constants
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};

const UNAVAILABILITY_REASONS = [
'Annual Leave',
'Adoptive Leave',
'Bereavement Leave',
'Carers Leave.',
'Certified Sick Leave',
'Force Majeure',
'Health and Safety Leave',
'Jury Leave',
'Maternity Leave',
'Other',
'Parents Leave',
'Parental Leave',
'Paternity Leave',
'Sick Leave',
'Study Leave / Mandatory Training',
];

const RollingRosterView = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  
  // State
  const [startDate, setStartDate] = useState(new Date());
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [selectedJobTitleFilter, setSelectedJobTitleFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedDay, setSelectedDay] = useState('monday');
  const [viewMode, setViewMode] = useState('create');
  
  // Add state for next period
  const [nextPeriodDialogOpen, setNextPeriodDialogOpen] = useState(false);
  const [nextStartDate, setNextStartDate] = useState(null);
  const [generatePeriodDialogOpen, setGeneratePeriodDialogOpen] = useState(false);
  const [selectedPeriodStartDate, setSelectedPeriodStartDate] = useState(null);
  
  // Add to your existing state variables
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  
  // Add state for multiple day selection
  const [selectedDays, setSelectedDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
  });
  
  // Add state for shift preset
  const [selectedPreset, setSelectedPreset] = useState('');

  // Add mutation hooks
  const [setNextGenerationDate, { isLoading: settingNextDate }] = useSetNextGenerationDateMutation();
  const [generateAvailabilitiesForPeriod, { isLoading: generatingPeriod }] = useGenerateAvailabilitiesForPeriodMutation();
  
  // Add state
  const [updatePeriodDialogOpen, setUpdatePeriodDialogOpen] = useState(false);
  const [newPeriodStartDate, setNewPeriodStartDate] = useState(null);


  // Add these new state variables near your other state declarations
const [setAllPeriodsDialogOpen, setSetAllPeriodsDialogOpen] = useState(false);
const [generateAllPeriodsDialogOpen, setGenerateAllPeriodsDialogOpen] = useState(false);
const [globalPeriodStartDate, setGlobalPeriodStartDate] = useState(new Date());
const [isSettingAllPeriods, setIsSettingAllPeriods] = useState(false);
const [isGeneratingAllPeriods, setIsGeneratingAllPeriods] = useState(false);




  // Week and day constants
  const weeks = [1, 2, 3, 4, 5, 6];
  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  // Helper functions
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const parseDateFromInput = (dateString) => {
    return new Date(dateString + 'T00:00:00');
  };
  
  const getDayNameFromDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return dayNames[dayIndex];
};

  const handleDayToggle = (day) => {
    setSelectedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  // Calculate week dates
const weekDates = useMemo(() => {
  let dateToUse;
  
  if (viewMode === 'edit' && selectedRoster && selectedRoster.startDate) {
    dateToUse = new Date(selectedRoster.startDate);
  } else {
    dateToUse = new Date(startDate);
  }
  
  if (!dateToUse || isNaN(dateToUse.getTime())) {
    console.warn('Invalid date for week calculation');
    return [];
  }
  
  const dates = [];
  
  // Find the Monday of the week containing the start date
  const dayOfWeek = dateToUse.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  
  const mondayDate = new Date(dateToUse);
  mondayDate.setDate(mondayDate.getDate() + daysToMonday);
  
  // Create 6 weeks starting from this Monday
  for (let week = 0; week < 6; week++) {
    const weekStart = new Date(mondayDate);
    weekStart.setDate(weekStart.getDate() + (week * 7));
    
    const weekDatesArray = [];
    for (let day = 0; day < 7; day++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + day);
      
      const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      
      // Map dayIndex to your day keys (monday=0, tuesday=1, etc., but adjust for Sunday)
      let dayKey;
      if (dayIndex === 0) {
        dayKey = 'sunday'; // Sunday
      } else {
        // For Monday (1) to Saturday (6), map directly to your array
        dayKey = days[dayIndex - 1]?.key || 'monday';
      }
      
      weekDatesArray.push({
        date: date.toISOString().split('T')[0],
        display: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        dayIndex: dayIndex,
        dayKey: dayKey
      });
    }
    
    dates.push({
      weekNumber: week + 1,
      startDate: weekStart.toISOString().split('T')[0],
      dates: weekDatesArray
    });
  }
  
  return dates;
}, [startDate, selectedRoster, viewMode, days]);
  
  const getDateForWeekDay = (weekNumber, dayKey) => {
  if (!weekDates[weekNumber - 1]) return null;
  
  const weekData = weekDates[weekNumber - 1];
  
  // Find the date for the specific day key in this week
  const dayData = weekData.dates.find(date => date.dayKey === dayKey);
  
  if (!dayData) return null;
  
  return {
    date: dayData.date,
    display: dayData.display,
    dayIndex: dayData.dayIndex
  };
};
  

// Handle setting new period for all rosters
const handleSetAllPeriods = async () => {
  try {
    if (!globalPeriodStartDate) {
      showSnackbar('Please select a start date for the new period', 'error');
      return;
    }
    
    setIsSettingAllPeriods(true);
    
    // Get all rosters
    const allRosters = rostersData?.data || [];
    
    if (allRosters.length === 0) {
      showSnackbar('No rosters found to update', 'warning');
      setIsSettingAllPeriods(false);
      setSetAllPeriodsDialogOpen(false);
      return;
    }
    
    // Update each roster with the new start date
    const updatePromises = allRosters.map(roster => 
      updateRollingRosterPeriod({
        id: roster._id,
        newStartDate: globalPeriodStartDate.toISOString()
      }).unwrap()
    );
    
    const results = await Promise.allSettled(updatePromises);
    
    // Count successes and failures
    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;
    
    if (failed > 0) {
      showSnackbar(`Updated ${successful} rosters, ${failed} failed`, 'warning');
    } else {
      showSnackbar(`Successfully updated all ${successful} rosters`, 'success');
    }
    
    // Refresh data
    refetchRosters();
    
    // Close dialog
    setSetAllPeriodsDialogOpen(false);
    
  } catch (error) {
    console.error('Error setting all periods:', error);
    showSnackbar(error?.data?.message || 'Failed to update all rosters', 'error');
  } finally {
    setIsSettingAllPeriods(false);
  }
};

// Handle generating availabilities for all rosters for the new period
const handleGenerateAllPeriods = async () => {
  try {
    if (!globalPeriodStartDate) {
      showSnackbar('Please select a start date for the generation period', 'error');
      return;
    }
    
    setIsGeneratingAllPeriods(true);
    
    // Get all rosters
    const allRosters = rostersData?.data || [];
    
    if (allRosters.length === 0) {
      showSnackbar('No rosters found to generate', 'warning');
      setIsGeneratingAllPeriods(false);
      setGenerateAllPeriodsDialogOpen(false);
      return;
    }
    
    // Generate availabilities for each roster for the new period
    const generatePromises = allRosters.map(roster => 
      generateAvailabilitiesForPeriod({
        id: roster._id,
        startDate: globalPeriodStartDate.toISOString(),
        createdBy: userInfo._id
      }).unwrap()
    );
    
    const results = await Promise.allSettled(generatePromises);
    
    // Count successes and calculate totals
    let totalCreated = 0;
    const successful = results.filter(r => {
      if (r.status === 'fulfilled') {
        totalCreated += r.value?.data?.createdCount || 0;
        return true;
      }
      return false;
    }).length;
    
    const failed = results.filter(r => r.status === 'rejected').length;
    
    if (failed > 0) {
      showSnackbar(
        `Generated ${totalCreated} availabilities across ${successful} rosters, ${failed} failed`, 
        'warning'
      );
    } else {
      showSnackbar(
        `Successfully generated ${totalCreated} availabilities across all ${successful} rosters`, 
        'success'
      );
    }
    
    // Refresh data
    refetchRosters();
    
    // Close dialog
    setGenerateAllPeriodsDialogOpen(false);
    
  } catch (error) {
    console.error('Error generating all periods:', error);
    showSnackbar(error?.data?.message || 'Failed to generate all periods', 'error');
  } finally {
    setIsGeneratingAllPeriods(false);
  }
};



  // API Hooks
  const { data: staffData, isLoading: loadingStaff } = useGetUsersQuery();
  const { data: rostersData, isLoading: loadingRosters, refetch: refetchRosters } = useGetRollingRostersQuery({
    page: 1,
    limit: 100
  });

  


  
  const [createRollingRoster, { isLoading: creating }] = useCreateRollingRosterMutation();
  const [updateRollingRoster, { isLoading: updating }] = useUpdateRollingRosterMutation();
  const [generateAvailabilities, { isLoading: generating }] = useGenerateAvailabilitiesMutation();
  const [deleteRollingRoster, { isLoading: deleting }] = useDeleteRollingRosterMutation();
  const [updateRollingRosterPeriod, { isLoading: updatingPeriod }] = useUpdateRollingRosterPeriodMutation();

  // Layout effect
  useEffect(() => {
    setFluid(true);
    return () => {
      setFluid(false);
    };
  }, [setFluid]);
  
  // Process staff data
  useEffect(() => {
    if (staffData) {
      const filtered = staffData.filter(user => !user.isAdmin && !user.isAgency);
      setStaffList(filtered);
      setFilteredStaff(filtered);
    }
  }, [staffData]);
  
  // Apply job title filter
  useEffect(() => {
    if (selectedJobTitleFilter === 'all') {
      setFilteredStaff(staffList);
    } else {
      const filtered = staffList.filter(staff => 
        staff.jobtitle === selectedJobTitleFilter
      );
      setFilteredStaff(filtered);
    }
  }, [selectedJobTitleFilter, staffList]);
  
  // Job titles for filter
  const jobTitles = useMemo(() => {
    const titles = [...new Set(staffList.map(staff => staff.jobtitle).filter(Boolean))].sort();
    return ['all', ...titles];
  }, [staffList]);
  
  // Handle create rolling roster
  const handleCreateRollingRoster = async () => {
    try {
      if (selectedStaff.length === 0) {
        showSnackbar('Please select at least one staff member', 'error');
        return;
      }
      
      const result = await createRollingRoster({
        startDate: startDate.toISOString(),
        staffList: selectedStaff.map(staff => staff._id),
        createdBy: userInfo._id
      }).unwrap();
      
      showSnackbar(`Created ${result.data.createdRosters.length} rolling rosters`, 'success');
      setCreateDialogOpen(false);
      setSelectedStaff([]);
      refetchRosters();
      
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to create rolling rosters', 'error');
    }
  };
  

  // Add these handler functions near your other handlers (around line 400)
const handleOpenSetAllPeriods = () => {
  setGlobalPeriodStartDate(new Date()); // Reset to current date
  setSetAllPeriodsDialogOpen(true);
};

const handleOpenGenerateAllPeriods = () => {
  setGlobalPeriodStartDate(new Date()); // Reset to current date
  setGenerateAllPeriodsDialogOpen(true);
};


  const handleUpdatePeriod = async () => {
    try {
      if (!selectedRoster || !newPeriodStartDate) {
        showSnackbar('Please select a new start date for the period', 'error');
        return;
      }
      
      const result = await updateRollingRosterPeriod({
        id: selectedRoster._id,
        newStartDate: newPeriodStartDate.toISOString()
      }).unwrap();
      
      showSnackbar('Rolling roster period updated successfully', 'success');
      setUpdatePeriodDialogOpen(false);
      setNewPeriodStartDate(null);
      
      if (result.data.roster) {
        setSelectedRoster(result.data.roster);
      }
      
      refetchRosters();
      
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to update period', 'error');
    }
  };

  // Handle edit availability for a specific day
  // Handle edit availability for a specific day
// Handle edit availability for a specific day
const handleEditDayAvailability = (roster, weekNumber, dayKey) => {
  setSelectedRoster(roster);
  setSelectedWeek(weekNumber);
  setSelectedDay(dayKey);
  setViewMode('edit');
  
  // Get existing data for the clicked day
  const weekKey = `week${weekNumber}`;
  const clickedDayData = roster[weekKey]?.[dayKey] || null;
  
 //console.log('=== DEBUG: Opening dialog ===');
 //console.log('Week:', weekNumber, 'Day:', dayKey);
 //console.log('Clicked day data:', clickedDayData);
  
  // Get the actual date for this day
  const dateInfo = getDateForWeekDay(weekNumber, dayKey);
  
  // SIMPLE APPROACH: Only select the clicked day
  const resetDays = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
  };
  
  // Always select only the clicked day
  resetDays[dayKey] = true;
  
 //console.log('Days to select (simple approach):', resetDays);
  
  setSelectedDays(resetDays);
  
  setSelectedDayData({
    ...clickedDayData,
    week: weekNumber,
    day: dayKey,
    date: dateInfo?.date || '',
    dateDisplay: dateInfo?.display || '',
    staffId: roster.employeeId._id || roster.employeeId,
    staffName: roster.employeeName,
    jobTitle: roster.jobTitle,
    ward: roster.ward,
    // Pass the selectedDays state to the dialog
    selectedDays: resetDays
  });
  
  setAvailabilityDialogOpen(true);
};
  
  // Handle shift preset selection
  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    
    if (presetKey && SHIFT_PRESETS[presetKey]) {
      const preset = SHIFT_PRESETS[presetKey];
      
      setSelectedDayData(prev => ({
        ...prev,
        startTime: preset.start,
        endTime: preset.end
      }));
    }
  };

   // Handle remove availability on double click
const handleRemoveAvailability = async (roster, weekNumber, dayKey) => {
  try {
    if (!roster || !window.confirm(`Remove availability for ${dayKey} in Week ${weekNumber}?`)) {
      return;
    }
    
    const weekKey = `week${weekNumber}`;
    
    // Start with existing week data
    const existingWeekData = roster[weekKey] || {};
    
    // Create updated week data with this day set to null
    const updatedWeekData = { ...existingWeekData };
    updatedWeekData[dayKey] = null;
    
    // Create the update object
    const updateData = {
      [weekKey]: updatedWeekData
    };
    
    // Send update request
    await updateRollingRoster({
      id: roster._id,
      ...updateData
    }).unwrap();
    
    // Perform optimistic update
    if (selectedRoster && selectedRoster._id === roster._id) {
      setSelectedRoster(prev => ({
        ...prev,
        [weekKey]: updatedWeekData
      }));
    }
    
    showSnackbar(`Availability removed for ${dayKey} in Week ${weekNumber}`, 'success');
    
    // Force refetch of roster data
    refetchRosters();
    
  } catch (error) {
    showSnackbar(error?.data?.message || 'Failed to remove availability', 'error');
    refetchRosters();
  }
};

  // Handle save availability with multiple days
 // Handle save availability with multiple days (only for current week)
const handleSaveAvailability = async (availabilityData) => {
  try {
    if (!selectedRoster) return;
    
   //console.log('=== DEBUG: Saving availability ===');
   //console.log('Availability data:', availabilityData);
   //console.log('Week:', selectedWeek);
   //console.log('Selected Roster ID:', selectedRoster._id);
    
    // Check if this is a removal
    const isRemoval = availabilityData.__isRemoval;
    
    // Extract selectedDays from the availabilityData
    const { __isRemoval, selectedDays: daysToUpdate, ...availabilityFields } = availabilityData;
    
   //console.log('Is removal?', isRemoval);
   //console.log('Days to update from dialog:', daysToUpdate);
    
    // Get the selected days from the form data
    const dayKeysToUpdate = daysToUpdate 
      ? Object.entries(daysToUpdate)
          .filter(([_, isSelected]) => isSelected)
          .map(([dayKey]) => dayKey)
      : []; // Return empty array if no days selected
    
   //console.log('Day keys to update:', dayKeysToUpdate);
    
    // Also check if parent selectedDays has any selections (as fallback)
    if (dayKeysToUpdate.length === 0) {
      // Check parent selectedDays as fallback
      const parentSelectedDays = Object.entries(selectedDays)
        .filter(([_, isSelected]) => isSelected)
        .map(([dayKey]) => dayKey);
      
     //console.log('Parent selected days:', parentSelectedDays);
      
      if (parentSelectedDays.length === 0) {
        showSnackbar('No days selected to update', 'error');
        return;
      }
      dayKeysToUpdate.push(...parentSelectedDays);
    }
    
   //console.log('Final day keys to update:', dayKeysToUpdate);
    
    // Create the complete update for the week
    const weekKey = `week${selectedWeek}`;
    
    // Start with existing week data or empty object
    const existingWeekData = selectedRoster[weekKey] || {};
    
    // Create updated week data
    const updatedWeekData = { ...existingWeekData };
    
    // Update each selected day
    for (const dayKey of dayKeysToUpdate) {
      if (isRemoval) {
        // For removal, set to null
        updatedWeekData[dayKey] = null;
      } else {
        // For update, set the availability fields
        updatedWeekData[dayKey] = availabilityFields;
      }
    }
    
    // Create the update object
    const updateData = {
      [weekKey]: updatedWeekData
    };
    
   //console.log('Complete update data:', updateData);
    
    // Send ONE update request with all days
    const result = await updateRollingRoster({
      id: selectedRoster._id,
      ...updateData
    }).unwrap();
    
   //console.log('Update result:', result);
    
    // Perform optimistic update
    setSelectedRoster(prev => ({
      ...prev,
      [weekKey]: updatedWeekData
    }));
    
    if (isRemoval) {
      showSnackbar(`Availability removed for ${dayKeysToUpdate.length} day(s) in Week ${selectedWeek}`, 'success');
    } else {
      showSnackbar(`Availability updated for ${dayKeysToUpdate.length} day(s) in Week ${selectedWeek}`, 'success');
    }
    
    setAvailabilityDialogOpen(false);
    
    // Force refetch of roster data
    refetchRosters();
    
  } catch (error) {
    console.error('Error saving availability:', error);
    showSnackbar(error?.data?.message || 'Failed to update availability', 'error');
    refetchRosters();
  }
};


  
  const handleGenerateAvailabilities = async (rosterId) => {
    try {
      const result = await generateAvailabilities({
        id: rosterId,
        createdBy: userInfo._id
      }).unwrap();
      
      showSnackbar(
        `Generated ${result.data.createdCount} new and updated ${result.data.updatedCount} existing availabilities`,
        'success'
      );
      
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to generate availabilities', 'error');
    }
  };
  
  const handleDeleteRoster = async (rosterId) => {
    if (window.confirm('Are you sure you want to delete this rolling roster?')) {
      try {
        await deleteRollingRoster(rosterId).unwrap();
        showSnackbar('Rolling roster deleted successfully', 'success');
        refetchRosters();
      } catch (error) {
        showSnackbar(error?.data?.message || 'Failed to delete rolling roster', 'error');
      }
    }
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleSetNextGenerationDate = async () => {
    try {
      if (!selectedRoster || !nextStartDate) {
        showSnackbar('Please select a start date for the next period', 'error');
        return;
      }
      
      const result = await setNextGenerationDate({
        id: selectedRoster._id,
        nextStartDate: nextStartDate.toISOString()
      }).unwrap();
      
      showSnackbar('Next generation date set successfully', 'success');
      setNextPeriodDialogOpen(false);
      setNextStartDate(null);
      refetchRosters();
      
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to set next generation date', 'error');
    }
  };

  const handleGenerateForPeriod = async (periodStartDate) => {
    try {
      if (!selectedRoster || !periodStartDate) {
        showSnackbar('Please select a period start date', 'error');
        return;
      }
      
      const result = await generateAvailabilitiesForPeriod({
        id: selectedRoster._id,
        startDate: periodStartDate.toISOString(),
        createdBy: userInfo._id
      }).unwrap();
      
      showSnackbar(
        `Generated ${result.data.createdCount} availabilities for period ${result.data.period.display}`,
        'success'
      );
      
      setGeneratePeriodDialogOpen(false);
      setSelectedPeriodStartDate(null);
      refetchRosters();
      
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to generate availabilities for period', 'error');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
const getDayDisplay = (roster, weekNumber, dayKey) => {
  if (!roster) {
    return (
      <Box sx={{ textAlign: 'center', color: '#999', fontStyle: 'italic', fontSize: '0.8rem' }}>
        No roster data
      </Box>
    );
  }
  
  const weekKey = `week${weekNumber}`;
  const dayData = roster[weekKey]?.[dayKey];
  
  if (!dayData || dayData === null) {
    return (
      <Box sx={{ textAlign: 'center', color: '#999', fontStyle: 'italic', fontSize: '0.8rem' }}>
        Not set
      </Box>
    );
  }
  
  const isUnavailable = dayData.isUnavailable || dayData.isSickLeave;
  
  // Use a ref to track click timing
  let clickTimer = null;
  
  const handleClick = () => {
    if (clickTimer === null) {
      // First click - start timer
      clickTimer = setTimeout(() => {
        // Single click after delay
        handleEditDayAvailability(roster, weekNumber, dayKey);
        clickTimer = null;
      }, 300); // 300ms delay to detect double click
    }
  };
  
  const handleDoubleClick = () => {
    // Clear the single click timer
    if (clickTimer) {
      clearTimeout(clickTimer);
      clickTimer = null;
    }
    // Double click - remove
    handleRemoveAvailability(roster, weekNumber, dayKey);
  };
  
  return (
    <Tooltip title={
      <Box>
        <div>{isUnavailable ? 
          `Unavailable: ${dayData.unavailabilityReason || dayData.sicknessReason || 'No reason'}` : 
          `Available: ${dayData.startTime || '08:00'} - ${dayData.endTime || '16:00'}`
        }</div>
        <div style={{ fontSize: '0.7rem', marginTop: '4px', color: '#ccc' }}>
          Click to edit
        </div>
      </Box>
    }>
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: isUnavailable ? 
            (dayData.isSickLeave ? '#f44336' : '#d32f2f') : 
            (dayData.status === 'Approved' ? '#4caf50' : '#2196f3'),
          color: 'white',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.9,
            boxShadow: '0 0 0 2px rgba(0,0,0,0.1)'
          }
        }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        {isUnavailable ? '⛔' : '✓'} {dayData.startTime || '--'} - {dayData.endTime || '--'}
      </Box>
    </Tooltip>
  );
};
  
  const formatRosterPeriod = (roster) => {
    if (!roster || !roster.startDate || !roster.endDate) {
      return 'Period not set';
    }
    
    try {
      const start = new Date(roster.startDate);
      const end = new Date(roster.endDate);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return 'Invalid period dates';
      }
      
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    } catch (error) {
      console.error('Error formatting roster period:', error);
      return 'Error formatting period';
    }
  };
  
  if (loadingStaff) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  // Custom Availability Dialog for Rolling Roster
  const RollingRosterAvailabilityDialog = ({ 
    open, 
    onClose, 
    onSave, 
    dayData,
    isEditing = false 
  }) => {

     // Add selectedDays to the component state
  const [localSelectedDays, setLocalSelectedDays] = React.useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
  });

    const [formData, setFormData] = React.useState({
      startTime: '08:00',
      endTime: '16:00',
      isUnavailable: false,
      unavailabilityReason: '',
      unavailabilityType: 'Full Day',
      unavailabilityDetails: '',
      isSickLeave: false,
      sickLeaveType: 'Full Day',
      sicknessReason: '',
      comments: '',
      categoryOfStaff: '',
      wardDepartmentArea: ''
    });

    React.useEffect(() => {
      if (dayData) {
        setFormData({
          startTime: dayData.startTime || '08:00',
          endTime: dayData.endTime || '16:00',
          isUnavailable: dayData.isUnavailable || false,
          unavailabilityReason: dayData.unavailabilityReason || '',
          unavailabilityType: dayData.unavailabilityType || 'Full Day',
          unavailabilityDetails: dayData.unavailabilityDetails || '',
          isSickLeave: dayData.isSickLeave || false,
          sickLeaveType: dayData.sickLeaveType || 'Full Day',
          sicknessReason: dayData.sicknessReason || '',
          comments: dayData.comments || '',
          categoryOfStaff: dayData.categoryOfStaff || dayData.jobTitle || '',
          wardDepartmentArea: dayData.ward || ''
        });

       if (dayData.selectedDays) {
        setLocalSelectedDays(dayData.selectedDays);
      }
    }
  }, [dayData]);


     

  // Create a local toggle function
  const handleLocalDayToggle = (day) => {
    setLocalSelectedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };


 


    const handleSubmit = () => {
      onSave({
      ...formData,
      // Include which days were selected
      selectedDays: localSelectedDays
    });
      onClose();
    };

    // Handle remove availability for selected days
const handleRemoveSelectedAvailability = () => {
  // Get the days to remove (selected days in the dialog)
  const daysToRemove = Object.entries(localSelectedDays)
    .filter(([_, isSelected]) => isSelected)
    .map(([dayKey]) => dayKey);
  
  if (daysToRemove.length === 0) {
    alert('No days selected to remove. Please select days in the "Apply to Multiple Days" section.');
    return;
  }

  if (!window.confirm(`Remove availability for ${daysToRemove.length} selected day(s)?`)) {
    return;
  }

   // For removal, we need to pass null values
  // The onSave function expects availability data, but we'll handle null specially
  const removalData = {
    __isRemoval: true, // Flag to indicate this is a removal
    selectedDays: localSelectedDays,
    // Include empty values
    startTime: '',
    endTime: '',
    isUnavailable: false,
    unavailabilityReason: '',
    unavailabilityType: 'Full Day',
    unavailabilityDetails: '',
    isSickLeave: false,
    sickLeaveType: 'Full Day',
    sicknessReason: '',
    comments: '',
    categoryOfStaff: '',
    wardDepartmentArea: ''
  };
  
  onSave(removalData);
  onClose();
};


    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit Availability' : 'Set Availability'}
          {dayData && (
            <>
              <Typography variant="subtitle2" color="textSecondary">
                {dayData.staffName} • {dayData.day} (Week {dayData.week})
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Editing Week {selectedWeek} • Clicked on: {dayData.day}
              </Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {/* Shift Presets */}
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WatchLater fontSize="small" />
              Quick Shift Presets
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                <Grid item xs={6} sm={4} key={key}>
                  <Button
                    fullWidth
                    variant={selectedPreset === key ? "contained" : "outlined"}
                    size="small"
                    onClick={() => handlePresetChange(key)}
                    sx={{ 
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {preset.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
            {selectedPreset && (
              <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                Selected: {SHIFT_PRESETS[selectedPreset].label}
              </Typography>
            )}
          </Box>
          
          {/* Multiple Day Selection */}
          <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday fontSize="small" />
              Apply to Multiple Days
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                Select days of the week to apply the same availability:
              </Typography>
              
             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
  {Object.entries({
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun'
  }).map(([dayKey, dayLabel]) => (
    <Box key={dayKey} sx={{ display: 'flex', alignItems: 'center' }}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={localSelectedDays[dayKey]} // Changed from selectedDays to localSelectedDays
            onChange={() => handleLocalDayToggle(dayKey)} // Changed from handleDayToggle to handleLocalDayToggle
            disabled={false}
            sx={{
              color: localSelectedDays[dayKey] ? 'primary.main' : undefined, // Changed here
              '&.Mui-checked': {
                color: localSelectedDays[dayKey] ? 'primary.main' : undefined, // Changed here
              }
            }}
          />
        }
        label={
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: localSelectedDays[dayKey] ? 'bold' : 'normal', // Changed here
              color: localSelectedDays[dayKey] ? 'primary.main' : 'text.primary' // Changed here
            }}
          >
            {dayLabel}
          </Typography>
        }
      />
    </Box>
  ))}
</Box>
              
              {/* Display selected days info */}
            
{Object.values(localSelectedDays).some(day => day) && ( // Changed from selectedDays to localSelectedDays
  <Box sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: 0.5 }}>
    <Typography variant="caption" color="primary" fontWeight="medium">
      This availability will be applied to Week {selectedWeek}: 
      {' '}
      {Object.entries(localSelectedDays) // Changed from selectedDays to localSelectedDays
        .filter(([_, isSelected]) => isSelected)
        .map(([dayKey, _]) => dayKey.charAt(0).toUpperCase() + dayKey.slice(1))
        .join(', ')}
    </Typography>
  </Box>
)}
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isUnavailable}
                    onChange={(e) => {
                      const isUnavailable = e.target.checked;
                      setFormData({
                        ...formData,
                        isUnavailable: isUnavailable,
                        unavailabilityReason: isUnavailable ? formData.unavailabilityReason : '',
                        unavailabilityType: isUnavailable ? 'Full Day' : 'Full Day'
                      });
                    }}
                    color="error"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 16, 
                      height: 16, 
                      bgcolor: 'error.light',
                      borderRadius: '2px',
                      border: '1px solid #d32f2f'
                    }} />
                    <Typography variant="body2" color={formData.isUnavailable ? 'error.main' : 'text.primary'}>
                      Mark as Unavailable
                    </Typography>
                    {formData.isUnavailable && (
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        bgcolor: 'error.light',
                        borderRadius: '2px',
                        border: '1px solid #d32f2f'
                      }} />
                    )}
                  </Box>
                }
              />
            </Grid>
            
            {formData.isUnavailable && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Unavailability Reason</InputLabel>
                    <Select
                      value={formData.unavailabilityReason}
                      label="Unavailability Reason"
                      onChange={(e) => setFormData({ ...formData, unavailabilityReason: e.target.value })}
                    >
                      <MenuItem value="">Select Reason...</MenuItem>
                      {UNAVAILABILITY_REASONS.map((reason) => (
                        <MenuItem key={reason} value={reason}>
                          {reason}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Unavailability Type</InputLabel>
                    <Select
                      value={formData.unavailabilityType}
                      label="Unavailability Type"
                      onChange={(e) => setFormData({ ...formData, unavailabilityType: e.target.value })}
                    >
                      <MenuItem value="Full Day">Full Day</MenuItem>
                      <MenuItem value="Half Day AM">Half Day (Morning)</MenuItem>
                      <MenuItem value="Half Day PM">Half Day (Afternoon)</MenuItem>
                      <MenuItem value="Partial Day">Partial Day</MenuItem>
                      <MenuItem value="Multi-Day">Multi-Day</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Unavailability Details (Optional)"
                    value={formData.unavailabilityDetails}
                    onChange={(e) => setFormData({ ...formData, unavailabilityDetails: e.target.value })}
                    multiline
                    rows={2}
                    placeholder="Additional details about the unavailability..."
                    size="small"
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comments (Optional)"
                value={formData.comments}
                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                multiline
                rows={3}
                placeholder="Any additional notes..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
  <Box>
    {isEditing && dayData && (
      <Button
        onClick={() => {
          handleRemoveSelectedAvailability();

          //if (window.confirm('Are you sure you want to remove this availability?')) {
            // Call a new function to handle removal
           // handleRemoveSelectedAvailability();
          //}
        }}
        color="error"
        variant="outlined"
        startIcon={<Delete />}
      >
        Remove
      </Button>
    )}
  </Box>
  <Box>
    <Button onClick={onClose}>Cancel</Button>
    <Button 
      onClick={handleSubmit} 
      variant="contained"
      sx={{ ml: 1 }}
    >
      {isEditing ? 'Update' : 'Save'}
    </Button>
  </Box>
</DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Navigation Breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <MuiLink
          component={RouterLink}
          to="/dashboard"
          underline="hover"
          color="inherit"
        >
          Dashboard
        </MuiLink>
        <Typography color="text.primary">6-Week Rolling Roster</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      {/* Header */}

<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
  <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
    6-Week Rolling Availability Roster
  </Typography>
  
  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
    {/* NEW: Set All Periods Button */}
    <Button
      variant="outlined"
      startIcon={<DateRange />}
      onClick={handleOpenSetAllPeriods}  
      disabled={loadingRosters || rostersData?.data?.length === 0}
      sx={{
        borderColor: '#ff9800',
        color: '#ff9800',
        '&:hover': {
          borderColor: '#f57c00',
          backgroundColor: 'rgba(255, 152, 0, 0.04)'
        }
      }}
    >
      Set All Periods
    </Button>
    
    {/* NEW: Generate All Periods Button */}
    <Button
      variant="contained"
      startIcon={<PlayArrow />}
      onClick={handleOpenGenerateAllPeriods}  
      disabled={loadingRosters || rostersData?.data?.length === 0 || generatingPeriod}
      sx={{
        bgcolor: '#4caf50',
        '&:hover': {
          bgcolor: '#388e3c'
        }
      }}
    >
      Generate All Periods
    </Button>
    
    <Button
      variant="contained"
      startIcon={<Add />}
      onClick={() => setCreateDialogOpen(true)}
      sx={{
        bgcolor: '#1976d2',
        '&:hover': {
          bgcolor: '#1565c0'
        }
      }}
    >
      Create New Roster
    </Button>
  </Box>
</Box>


      
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Manage Rosters" />
          <Tab label="Create New" />
        </Tabs>
      </Box>
      
      {/* Manage Rosters Tab */}
      {activeTab === 0 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Existing Rolling Rosters
          </Typography>
          
          {loadingRosters ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : rostersData?.data?.length === 0 ? (
            <Alert severity="info">
              No rolling rosters found. Create a new one to get started.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Staff Member</TableCell>
                    <TableCell>Job Title</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Next Generation</TableCell>
                    <TableCell>Generated Periods</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rostersData?.data?.map((roster) => (
                    <TableRow key={roster._id}>
                      <TableCell>
                        <Typography fontWeight="bold">{roster.employeeName}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {roster.employeeEmail}
                        </Typography>
                      </TableCell>
                      <TableCell>{roster.jobTitle}</TableCell>
                      <TableCell>
                        {new Date(roster.startDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(roster.endDate).toLocaleDateString()}
                      </TableCell>

                      <TableCell>
                        {roster.nextGenerationStartDate ? (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {new Date(roster.nextGenerationStartDate).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Scheduled
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Not scheduled
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {[1, 2, 3, 4, 5, 6].map(week => {
                          const weekKey = `week${week}`;
                          const hasData = Object.values(roster[weekKey] || {}).some(val => val !== null);
                          return (
                            <Chip
                              key={week}
                              label={`W${week}`}
                              size="small"
                              color={hasData ? 'success' : 'default'}
                              variant={hasData ? 'filled' : 'outlined'}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          );
                        })}
                      </TableCell>
                      
                      <TableCell>
                        {roster.generatedPeriods?.length || 0} periods
                      </TableCell>
                      <TableCell>
                        {roster.createdBy?.name || 'System'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="Print Schedule">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedRoster(roster);
                                setPrintDialogOpen(true);
                              }}
                            >
                              <Print fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="View/Edit Roster">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedRoster(roster);
                                setViewMode('edit');
                                setActiveTab(1);
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Generate Availabilities">
                            <IconButton
                              size="small"
                              onClick={() => handleGenerateAvailabilities(roster._id)}
                              disabled={generating}
                            >
                              <PlayArrow fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Delete Roster">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRoster(roster._id)}
                              disabled={deleting}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
      
      {/* Create/Edit Tab */}
      {activeTab === 1 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          {viewMode === 'edit' && selectedRoster ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6">
                    Editing Roster for: {selectedRoster.employeeName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedRoster.jobTitle} • {selectedRoster.ward || 'No Ward'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Period: {formatRosterPeriod(selectedRoster)}
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={() => handleGenerateAvailabilities(selectedRoster._id)}
                  disabled={generating}
                  sx={{
                    bgcolor: '#4caf50',
                    '&:hover': {
                      bgcolor: '#388e3c'
                    }
                  }}
                >
                  Generate Availabilities
                </Button>

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CalendarToday />}
                    onClick={() => {
                      setNewPeriodStartDate(new Date());
                      setUpdatePeriodDialogOpen(true);
                    }}
                  >
                    Update Period
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<PlayArrow />}
                    onClick={() => {
                      setSelectedPeriodStartDate(new Date());
                      setGeneratePeriodDialogOpen(true);
                    }}
                  >
                    Generate for Period
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<Print />}
                    onClick={() => setPrintDialogOpen(true)}
                    disabled={!selectedRoster}
                  >
                    Print Schedule
                  </Button>
                </Box>
              </Box>
              
              {/* Week Navigation */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton
                  onClick={() => setSelectedWeek(prev => Math.max(1, prev - 1))}
                  disabled={selectedWeek === 1}
                >
                  <ArrowBack />
                </IconButton>
                
                <Typography variant="h5" fontWeight="bold">
                  Week {selectedWeek}
                </Typography>
                
                <IconButton
                  onClick={() => setSelectedWeek(prev => Math.min(6, prev + 1))}
                  disabled={selectedWeek === 6}
                >
                  <ArrowForward />
                </IconButton>
              </Box>
              
              {/* Week Dates Display */}
              {weekDates[selectedWeek - 1] && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Week {selectedWeek} Dates:
                  </Typography>
                  <Grid container spacing={1}>
                    {weekDates[selectedWeek - 1].dates.map((dateInfo, index) => (
                      <Grid item xs key={dateInfo.date}>
                        <Card sx={{ textAlign: 'center', p: 1 }}>
                          <Typography variant="caption" color="textSecondary">
                            {days.find(d => d.key === dateInfo.dayKey)?.label || 'Day'}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {dateInfo.display}
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              
              {/* Week Grid */}
              <Grid container spacing={2}>
                {days.map((day) => {
                  const dateInfo = getDateForWeekDay(selectedWeek, day.key);
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={1.7} key={day.key}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 3,
                            borderColor: 'primary.main'
                          }
                        }}
                        onClick={() => handleEditDayAvailability(selectedRoster, selectedWeek, day.key)}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                            {day.label}
                          </Typography>
                          
                          {dateInfo && (
                            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                              {dateInfo.display}
                            </Typography>
                          )}
                          
                          {getDayDisplay(selectedRoster, selectedWeek, day.key)}
                          
                          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                            Click to edit
                          </Typography>


                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              
              {/* Week Selector */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 1 }}>
                {weeks.map(week => (
                  <Button
                    key={week}
                    variant={selectedWeek === week ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setSelectedWeek(week)}
                  >
                    Week {week}
                  </Button>
                ))}
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                Create New 6-Week Rolling Roster
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                This will create a 6-week rolling availability roster starting from the selected date.
                You can then set availability/unavailability for each day of each week.
              </Alert>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={formatDateForInput(startDate)}
                    onChange={(e) => {
                      const newDate = parseDateFromInput(e.target.value);
                      setStartDate(newDate);
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    Note: Week will start on the Monday of the selected week
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Filter Staff by Job Title</InputLabel>
                    <Select
                      value={selectedJobTitleFilter}
                      label="Filter Staff by Job Title"
                      onChange={(e) => setSelectedJobTitleFilter(e.target.value)}
                    >
                      {jobTitles.map(title => (
                        <MenuItem key={title} value={title}>
                          {title === 'all' ? 'All Job Titles' : title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Select Staff Members ({selectedStaff.length} selected)
                  </Typography>
                  
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: '300px', overflow: 'auto' }}>
                    <Grid container spacing={1}>
                      {filteredStaff.map((staff) => (
                        <Grid item xs={12} sm={6} md={4} key={staff._id}>
                          <Card 
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              borderColor: selectedStaff.some(s => s._id === staff._id) ? 'primary.main' : 'divider',
                              bgcolor: selectedStaff.some(s => s._id === staff._id) ? 'primary.50' : 'white'
                            }}
                            onClick={() => {
                              setSelectedStaff(prev => {
                                const exists = prev.some(s => s._id === staff._id);
                                if (exists) {
                                  return prev.filter(s => s._id !== staff._id);
                                } else {
                                  return [...prev, staff];
                                }
                              });
                            }}
                          >
                            <CardContent sx={{ p: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Checkbox
                                  checked={selectedStaff.some(s => s._id === staff._id)}
                                  size="small"
                                  readOnly
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {staff.name}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {staff.jobtitle} • {staff.ward || 'No Ward'}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      {selectedStaff.length} staff selected
                    </Typography>
                    
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleCreateRollingRoster}
                      disabled={selectedStaff.length === 0 || creating}
                      sx={{
                        bgcolor: '#1976d2',
                        '&:hover': {
                          bgcolor: '#1565c0'
                        }
                      }}
                    >
                      {creating ? 'Creating...' : 'Create Rolling Roster'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      )}
      


      {/* Set All Periods Dialog */}
<Dialog open={setAllPeriodsDialogOpen} onClose={() => setSetAllPeriodsDialogOpen(false)} maxWidth="sm" fullWidth>
  <DialogTitle>
    Set New Period for All Rosters
    <Typography variant="subtitle2" color="textSecondary">
      This will update the start date for all existing 6-week rosters
    </Typography>
  </DialogTitle>
  <DialogContent>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
      Select a new start date. All rosters will be updated to start from this date.
      The 6-week period will begin on the Monday of the week containing this date.
    </Typography>
    
    <TextField
      fullWidth
      label="New Period Start Date"
      type="date"
      value={formatDateForInput(globalPeriodStartDate)}
      onChange={(e) => setGlobalPeriodStartDate(parseDateFromInput(e.target.value))}
      InputLabelProps={{ shrink: true }}
      sx={{ mt: 2 }}
    />
    
    {globalPeriodStartDate && (
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          New 6-week period will be:
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {(() => {
            // Calculate Monday of the week
            const date = new Date(globalPeriodStartDate);
            const dayOfWeek = date.getDay();
            const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            const mondayDate = new Date(date);
            mondayDate.setDate(mondayDate.getDate() + daysToMonday);
            
            const endDate = new Date(mondayDate);
            endDate.setDate(endDate.getDate() + (6 * 7) - 1);
            
            return `${mondayDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
          })()}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          This will update {rostersData?.data?.length || 0} roster(s)
        </Typography>
      </Box>
    )}
    
    <Alert severity="warning" sx={{ mt: 3 }}>
      <Typography variant="body2">
        <strong>Warning:</strong> This action will update the start date for ALL existing rosters.
        Previously generated availabilities may be affected.
      </Typography>
    </Alert>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setSetAllPeriodsDialogOpen(false)}>Cancel</Button>
    <Button 
      onClick={handleSetAllPeriods} 
      variant="contained" 
      disabled={!globalPeriodStartDate || isSettingAllPeriods}
      sx={{
        bgcolor: '#ff9800',
        '&:hover': {
          bgcolor: '#f57c00'
        }
      }}
    >
      {isSettingAllPeriods ? <CircularProgress size={24} /> : 'Set All Periods'}
    </Button>
  </DialogActions>
</Dialog>

{/* Generate All Periods Dialog */}
<Dialog open={generateAllPeriodsDialogOpen} onClose={() => setGenerateAllPeriodsDialogOpen(false)} maxWidth="sm" fullWidth>
  <DialogTitle>
    Generate Availabilities for All Rosters
    <Typography variant="subtitle2" color="textSecondary">
      Create availabilities for the new period across all rosters
    </Typography>
  </DialogTitle>
  <DialogContent>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
      This will generate availabilities for ALL rosters for the selected 6-week period.
      The availabilities will be created using each roster's existing patterns.
    </Typography>
    
    <TextField
      fullWidth
      label="Period Start Date"
      type="date"
      value={formatDateForInput(globalPeriodStartDate)}
      onChange={(e) => setGlobalPeriodStartDate(parseDateFromInput(e.target.value))}
      InputLabelProps={{ shrink: true }}
      sx={{ mt: 2 }}
    />
    
    {globalPeriodStartDate && (
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Will generate availabilities for:
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          {(() => {
            // Calculate Monday of the week
            const date = new Date(globalPeriodStartDate);
            const dayOfWeek = date.getDay();
            const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            const mondayDate = new Date(date);
            mondayDate.setDate(mondayDate.getDate() + daysToMonday);
            
            const endDate = new Date(mondayDate);
            endDate.setDate(endDate.getDate() + (6 * 7) - 1);
            
            return `${mondayDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
          })()}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Rosters to generate:</strong> {rostersData?.data?.length || 0}
          </Typography>
          <Typography variant="body2">
            <strong>Staff members:</strong> {rostersData?.data?.map(r => r.employeeName).join(', ')}
          </Typography>
        </Box>
      </Box>
    )}
    
    <Alert severity="info" sx={{ mt: 3 }}>
      <Typography variant="body2">
        This may take a few moments depending on the number of rosters. 
        The process will run in the background.
      </Typography>
    </Alert>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setGenerateAllPeriodsDialogOpen(false)}>Cancel</Button>
    <Button 
      onClick={handleGenerateAllPeriods} 
      variant="contained" 
      disabled={!globalPeriodStartDate || isGeneratingAllPeriods}
      sx={{
        bgcolor: '#4caf50',
        '&:hover': {
          bgcolor: '#388e3c'
        }
      }}
    >
      {isGeneratingAllPeriods ? <CircularProgress size={24} /> : 'Generate All Periods'}
    </Button>
  </DialogActions>
</Dialog>




      {/* Update Period Dialog */}
      <Dialog open={updatePeriodDialogOpen} onClose={() => setUpdatePeriodDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Rolling Roster Period</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Update the current 6-week period for {selectedRoster?.employeeName}. 
            This will change when the "Generate Availabilities" button works from.
          </Typography>
          
          <TextField
            fullWidth
            label="New Period Start Date"
            type="date"
            value={newPeriodStartDate ? formatDateForInput(newPeriodStartDate) : ''}
            onChange={(e) => setNewPeriodStartDate(parseDateFromInput(e.target.value))}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
          
          {newPeriodStartDate && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                New 6-week period will be:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {newPeriodStartDate.toLocaleDateString()} - {
                  new Date(new Date(newPeriodStartDate).setDate(newPeriodStartDate.getDate() + (6 * 7) - 1)).toLocaleDateString()
                }
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                The "Generate Availabilities" button will create availabilities for this period.
              </Typography>
            </Box>
          )}
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Current period: {formatRosterPeriod(selectedRoster)}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdatePeriodDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdatePeriod} 
            variant="contained" 
            disabled={!newPeriodStartDate || updatingPeriod}
          >
            {updatingPeriod ? 'Updating...' : 'Update Period'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create 6-Week Rolling Roster</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formatDateForInput(startDate)}
                onChange={(e) => setStartDate(parseDateFromInput(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Select Staff Members
              </Typography>
              
              <Autocomplete
                multiple
                options={filteredStaff}
                getOptionLabel={(option) => `${option.name} - ${option.jobtitle} (${option.ward || 'No Ward'})`}
                value={selectedStaff}
                onChange={(event, newValue) => {
                  setSelectedStaff(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Staff"
                    placeholder="Choose staff members..."
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {option.jobtitle} • {option.ward || 'No Ward'}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateRollingRoster} 
            variant="contained" 
            disabled={selectedStaff.length === 0 || creating}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Custom Availability Dialog with multiple day selection */}
      <RollingRosterAvailabilityDialog
        open={availabilityDialogOpen}
        onClose={() => {
          setAvailabilityDialogOpen(false);
          setSelectedDays({
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false
          });
          setSelectedPreset('');
        }}
        onSave={handleSaveAvailability}
        dayData={selectedDayData}
        isEditing={true}
      />

      {/* Set Next Period Dialog */}
      <Dialog open={nextPeriodDialogOpen} onClose={() => setNextPeriodDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Next Generation Period</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            This will set when the next 6-week period should start for {selectedRoster?.employeeName}
          </Typography>
          
          <TextField
            fullWidth
            label="Next Period Start Date"
            type="date"
            value={nextStartDate ? formatDateForInput(nextStartDate) : ''}
            onChange={(e) => setNextStartDate(parseDateFromInput(e.target.value))}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
          
          {nextStartDate && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                Next 6-week period will be:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {nextStartDate.toLocaleDateString()} - {
                  new Date(new Date(nextStartDate).setDate(nextStartDate.getDate() + (6 * 7) - 1)).toLocaleDateString()
                }
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNextPeriodDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSetNextGenerationDate} 
            variant="contained" 
            disabled={!nextStartDate || settingNextDate}
          >
            {settingNextDate ? 'Setting...' : 'Set Next Period'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate for Specific Period Dialog */}
      <Dialog open={generatePeriodDialogOpen} onClose={() => setGeneratePeriodDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Availabilities for Period</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Generate availabilities for a specific 6-week period starting from:
          </Typography>
          
          <TextField
            fullWidth
            label="Period Start Date"
            type="date"
            value={selectedPeriodStartDate ? formatDateForInput(selectedPeriodStartDate) : ''}
            onChange={(e) => setSelectedPeriodStartDate(parseDateFromInput(e.target.value))}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
          
          {selectedPeriodStartDate && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                This will generate availabilities for:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedPeriodStartDate.toLocaleDateString()} - {
                  new Date(new Date(selectedPeriodStartDate).setDate(selectedPeriodStartDate.getDate() + (6 * 7) - 1)).toLocaleDateString()
                }
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Using the template from {selectedRoster?.employeeName}'s rolling roster
              </Typography>
            </Box>
          )}
          
          {/* Show previously generated periods */}
          {selectedRoster?.generatedPeriods && selectedRoster.generatedPeriods.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Previously Generated Periods:
              </Typography>
              {selectedRoster.generatedPeriods.slice().reverse().map((period, index) => (
                <Chip
                  key={index}
                  label={`${new Date(period.startDate).toLocaleDateString()} - ${new Date(period.endDate).toLocaleDateString()}`}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGeneratePeriodDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleGenerateForPeriod(selectedPeriodStartDate)} 
            variant="contained" 
            disabled={!selectedPeriodStartDate || generatingPeriod}
          >
            {generatingPeriod ? 'Generating...' : 'Generate for Period'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Print Dialog */}
      <Dialog 
        open={printDialogOpen} 
        onClose={() => setPrintDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        fullScreen={isPrinting}
      >
        <DialogTitle>
          Print 6-Week Rolling Schedule
          <Typography variant="subtitle2" color="textSecondary">
            {selectedRoster?.employeeName} • {formatRosterPeriod(selectedRoster)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRoster && (
            <Box id="print-content">
              <RollingRosterPrintView 
                roster={selectedRoster} 
                startDate={selectedRoster.startDate} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Tip: Use landscape orientation for best printing results
            </Typography>
          </Box>
          <Box>
            <Button onClick={() => setPrintDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={() => {
                setIsPrinting(true);
                setTimeout(() => {
                  window.print();
                  setIsPrinting(false);
                  setPrintDialogOpen(false);
                }, 100);
              }}
              sx={{ ml: 1 }}
            >
              Print
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RollingRosterView;